import type {
  FilterConditionPluginSpecification,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

export const INTUITIVE_FILE_OF_TRUTH_PROCEDURE_NAME_CONDITION_ID =
  'intuitiveFotProcedureName';
export const FilterConditionIntuitiveFileOfTruthProcedureNamePluginType: FilterConditionPluginSpecification<
  Omit<JsonIntuitiveFileOfTruthProcedureNamePluginDto, 'type'>,
  JsonIntuitiveFileOfTruthProcedureNamePluginDto,
  IntuitiveFileOfTruthProcedureNameCondition
> = {
  type: 'intuitiveFotProcedureName',
  category: 'case',
  displayName: {
    de: 'Intuitive File of Truth Procedure Name',
    en: 'Intuitive File of Truth Procedure Name',
  },
  sortingIndex: 8,
  occurrence: 'single',
  newInstance(): FilterConditionIntuitiveFileOfTruthProcedureNamePlugin {
    return new FilterConditionIntuitiveFileOfTruthProcedureNamePlugin([], null);
  },
  fromCondition(
    jsonObj: IntuitiveFileOfTruthProcedureNameCondition,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthProcedureNamePluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthProcedureNamePlugin(
        jsonObj.includes ? jsonObj.includes : null,
        jsonObj.excludes ? jsonObj.excludes : null,
      );
  },
  fromObject(
    obj: JsonIntuitiveFileOfTruthProcedureNamePluginDto,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthProcedureNamePluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthProcedureNamePlugin(
        obj.includes ? obj.includes : null,
        obj.excludes ? obj.excludes : null,
      );
  },
};

export class FilterConditionIntuitiveFileOfTruthProcedureNamePlugin extends FilterConditionPluginImpl<
  Omit<JsonIntuitiveFileOfTruthProcedureNamePluginDto, 'type'>,
  JsonIntuitiveFileOfTruthProcedureNamePluginDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    Omit<JsonIntuitiveFileOfTruthProcedureNamePluginDto, 'type'>,
    JsonIntuitiveFileOfTruthProcedureNamePluginDto,
    IntuitiveFileOfTruthProcedureNameCondition
  > = FilterConditionIntuitiveFileOfTruthProcedureNamePluginType;

  constructor(
    protected includes: string[],
    protected excludes: string[],
  ) {
    super(FilterConditionIntuitiveFileOfTruthProcedureNamePluginType.type);
  }

  override equals(
    other: FilterConditionIntuitiveFileOfTruthProcedureNamePlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  clone(): FilterConditionIntuitiveFileOfTruthProcedureNamePlugin {
    return new FilterConditionIntuitiveFileOfTruthProcedureNamePlugin(
      this.includes ? [...this.includes] : null,
      this.excludes ? [...this.excludes] : null,
    );
  }

  toCondition(): BdCondition | null {
    return new IntuitiveFileOfTruthProcedureNameCondition(
        this.includes,
        this.excludes,
      );
  }

  toJSON(): JsonIntuitiveFileOfTruthProcedureNamePluginDto | null {
    return {
      type: this.type,
      includes: this.includes ? this.includes : null,
      excludes: this.excludes ? this.excludes : null,
    };
  }

  toValue(): Omit<JsonIntuitiveFileOfTruthProcedureNamePluginDto, 'type'> {
    return {
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override valid(): boolean {
    return (this.includes && this.includes.length > 0) || (this.excludes && this.excludes.length > 0);
  }

  validationToText(): string {
    if (!this.includes && !this.excludes) {
      return 'Mindestens eine Intuitive File of Truth Procedure Name auswählen';
    }

    if (this.includes && this.includes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Procedure Name auswählen';
    } else if (this.excludes && this.excludes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Procedure Name auswählen';
    } else {
      return '';
    }
  }

  toText(): string | null {
    if (!this.includes && !this.excludes) {
      return '';
    }

    if (this.excludes) {
      return this.excludes.length === 1
        ? 'ein Intuitive File of Truth Procedure Name'
        : `${this.excludes.length} Intuitive File of Truth Procedure Names`;
    }

    return this.includes.length === 1
      ? 'ein Intuitive File of Truth Procedure Name'
      : `${this.includes.length} Intuitive File of Truth Procedure Names`;
  }
}

class IntuitiveFileOfTruthProcedureNameCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(
      INTUITIVE_FILE_OF_TRUTH_PROCEDURE_NAME_CONDITION_ID,
      includes,
      excludes,
    );
  }
}

interface JsonIntuitiveFileOfTruthProcedureNamePluginDto {
  type: string;
  includes: string[];
  excludes: string[];
}
